import type { ElementType } from "@soft-db/vue-type-extended";
import { isRef, type ComputedRef, type NativeElements, type SetupContext, type VNode } from "vue";

type ForProps<T, U> = {
	each: T[] | null | undefined;
	"v-slots": (item: T, index: number) => U;
};

export function For<T, U extends JSX.Element | JSX.Element[] | null | string>(
	props: ForProps<T, U>,
	{ slots }: SetupContext
) {
	return (props.each?.map((val, i) => slots.default?.(val, i)[0]).filter((v): v is VNode => !!v) ||
		null) as unknown as JSX.Element;
}

type ShowProps<T extends ElementType, U> = {
	tag?: T;
	when?: U | boolean | null | undefined;
	"v-slots"?:
		| JSX.Element
		| string
		| JSX.Element[]
		| boolean
		| ((item: NonNullable<U>) => JSX.Element);
} & NativeElements[T];

/**
 * Show the element based on a condition. By default, render a `Fragment`.
 *
 * @returns
 */
export function Show<T extends ElementType, U>(props: ShowProps<T, U>, { slots }: SetupContext) {
	if (!props.when) return null;

	const defaultSlot = slots.default?.(props.when);
	if (props.tag) {
		const propsCopy = { ...props };
		delete propsCopy.tag;
		delete propsCopy.when;
		delete propsCopy["v-slots"];

		return <props.tag {...(propsCopy as any)}>{defaultSlot}</props.tag>;
	} else {
		return (defaultSlot?.length === 1 ? defaultSlot.at(0) : <>{defaultSlot}</>) || null;
	}
}

type SwitchProps = {
	"v-slots": JSX.Element | JSX.Element[];
};

export function Switch(_: SwitchProps, { slots }: SetupContext) {
	const matches = slots.default?.();

	if (!matches) return null;

	let children: VNode | null = null;
	for (const match of matches) {
		const props = match.props as MatchProps<any> | null;
		const condition = props?.when;

		// The condition is true if it is null
		if (
			props?.default ||
			(isRef(condition) && condition.value) ||
			(typeof condition === "function" && condition()) ||
			!!condition
		) {
			children = match;
			break;
		}
	}

	return children;
}

type MatchValue<T> = T | boolean | null | undefined;

type MatchProps<T> = {
	when?: MatchValue<T> | (() => MatchValue<T>) | ComputedRef<MatchValue<T>>;
	default?: boolean;
	"v-slots"?:
		| JSX.Element
		| string
		| JSX.Element[]
		| boolean
		| ((item: NonNullable<T>) => JSX.Element);
};
export function Match<T>(props: MatchProps<T>, { slots }: SetupContext) {
	const childrens = slots.default?.(props.when);

	if (childrens == null) return null;

	if (childrens.length === 1) {
		return childrens.at(0) || null;
	}

	return <>{childrens}</>;
}
